import type { Medal } from '@tg/shared';
import { fade, n, shade, shadowInk, star, tint, type ArtPal } from '../covers/kit';

/*
 * Medal frame of an achievement badge (viewBox 128): a metal ring with volume (light top-left, rim darker
 * bottom-right), an inner bevel, a calm inner field where the icon lives (r = 47) and a soft shadow below.
 * Tiers differ by metal AND by the number of small stars on the ring (1 / 2 / 3) — readable without colour.
 * "Not earned" is a muted slate ring without stars and without any "almost there" glow.
 */
interface Metal {
  base: string;
  rim: string;
  /** inner field tint */
  field: string;
}

const METAL: Record<'dark' | 'light', Record<Medal | 'none', Metal>> = {
  dark: {
    bronze: { base: '#C97A43', rim: '#6E3B18', field: '#2A1E1A' },
    silver: { base: '#BCC6D4', rim: '#5D6878', field: '#1C2433' },
    gold: { base: '#F2BE45', rim: '#8A5E0C', field: '#2A2414' },
    none: { base: '#3C475B', rim: '#232C3C', field: '#121A28' },
  },
  light: {
    bronze: { base: '#C87137', rim: '#7A3E14', field: '#FBEFE6' },
    silver: { base: '#B4BFCE', rim: '#687487', field: '#F1F4F8' },
    gold: { base: '#EDB32C', rim: '#946408', field: '#FFF6DC' },
    none: { base: '#C3C9D3', rim: '#8D96A5', field: '#EEF0F4' },
  },
};

const PIPS: Record<Medal, number> = { bronze: 1, silver: 2, gold: 3 };

export function metalOf(P: ArtPal, medal: Medal | null): Metal {
  return METAL[P.dark ? 'dark' : 'light'][medal ?? 'none'];
}

/** Frame markup; the icon is drawn on top of it inside the circle r = 47 around (64, 64). */
export function frameMarkup(P: ArtPal, p: string, medal: Medal | null): string {
  const m = metalOf(P, medal);
  const hi = tint(m.base, medal ? 0.6 : 0.3);
  const lo = shade(m.base, medal ? 0.35 : 0.25);
  const k = medal ? PIPS[medal] : 0;
  const pips: string[] = [];
  for (let i = 0; i < k; i++) {
    const a = Math.PI / 2 + (i - (k - 1) / 2) * 0.3;
    pips.push(
      `<polygon points="${star(64 + 54 * Math.cos(a), 64 + 54 * Math.sin(a), 5.2, 2, 4)}" fill="${tint(m.base, 0.75)}" stroke="${m.rim}" stroke-width="1.1" stroke-linejoin="round"/>`,
    );
  }
  return (
    `<defs>` +
    `<linearGradient id="${p}m" x1=".2" y1="0" x2=".75" y2="1"><stop offset="0" stop-color="${hi}"/><stop offset=".4" stop-color="${m.base}"/><stop offset=".8" stop-color="${shade(m.base, 0.18)}"/><stop offset="1" stop-color="${lo}"/></linearGradient>` +
    `<linearGradient id="${p}v" x1=".75" y1="1" x2=".2" y2="0"><stop offset="0" stop-color="${hi}"/><stop offset=".5" stop-color="${m.base}"/><stop offset="1" stop-color="${lo}"/></linearGradient>` +
    `<radialGradient id="${p}f" cx=".38" cy=".32" r=".8"><stop offset="0" stop-color="${tint(m.field, P.dark ? 0.1 : 0.6)}"/><stop offset="1" stop-color="${P.dark ? shade(m.field, 0.3) : shade(m.field, 0.05)}"/></radialGradient>` +
    fade(`${p}s`, shadowInk(P), P.dark ? 0.55 : 0.2) +
    `</defs>` +
    `<ellipse cx="64" cy="121" rx="44" ry="6" fill="url(#${p}s)"/>` +
    `<circle cx="64" cy="64" r="59" fill="url(#${p}m)" stroke="${m.rim}" stroke-width="2.5"/>` +
    `<circle cx="64" cy="64" r="49.5" fill="url(#${p}f)" stroke="url(#${p}v)" stroke-width="4"/>` +
    `<path d="M${n(64 - 55 * Math.cos(0.5))} ${n(64 - 55 * Math.sin(0.5))}A55 55 0 0 1 ${n(64 - 55 * Math.cos(1.45))} ${n(64 - 55 * Math.sin(1.45))}" fill="none" stroke="#FFFFFF" stroke-opacity="${medal ? 0.6 : 0.25}" stroke-width="3" stroke-linecap="round"/>` +
    pips.join('')
  );
}
